// Reset password page component
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import authService from '../services/authService';
import { useLanguage } from '../contexts/LanguageContext';
import './LoginPage.css';

function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [token, setToken] = useState(searchParams.get('token') || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const [tokenEmail, setTokenEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validateToken = useCallback(async () => {
    if (!token) return;
    
    setIsValidating(true);
    setError('');
    
    try {
      const data = await authService.verifyResetToken(token);
      setTokenValid(true);
      setTokenEmail(data.email);
    } catch (error) {
      setTokenValid(false);
      setError(t('auth.invalidToken'));
    } finally {
      setIsValidating(false);
    }
  }, [token]);

  // Validate token when component mounts or token changes
  useEffect(() => {
    if (token) {
      validateToken();
    }
  }, [token, validateToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError(t('auth.passwordMismatch'));
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await authService.resetPassword(token, password);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (error) {
      setError(error.message || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="login-page">
        <div className="login-welcome-content">
          <div className="login-form-section">
            <div className="auth-form-container">
              <div className="auth-form">
                <div className="auth-logo-container">
                  <Link to="/">
                    <img 
                      src="/images/taroca_logo.png" 
                      alt="Taroca Logo" 
                      className="auth-logo"
                    />
                  </Link>
                </div>
                <div className="auth-success-message">
                  <h2 className="auth-form-title">{t('auth.resetSuccess')}</h2>
                  <p className="auth-form-subtitle">
                    {t('auth.resetSuccess')}
                  </p>
                  <p className="auth-form-text">
                    Redirecting to login page in 3 seconds...
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="auth-submit-btn"
                  >
                    Go to Sign In
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-welcome-content">
        <div className="login-decoration">
          <img 
            src="/images/tarot-cards/image_21.jpg" 
            alt="Tarot Card" 
            className="login-tarot-image"
          />
        </div>
        <div className="login-form-section">
          <div className="auth-form-container">
            <div className="auth-form">
              <div className="auth-logo-container">
                <Link to="/">
                  <img 
                    src="/images/taroca_logo.png" 
                    alt="Taroca Logo" 
                    className="auth-logo"
                  />
                </Link>
              </div>
              <h2 className="auth-form-title">{t('auth.resetTitle')}</h2>
              {tokenEmail && (
                <p className="auth-form-subtitle">
                  Resetting password for: <strong>{tokenEmail}</strong>
                </p>
              )}

              {!token && (
                <div className="form-group">
                  <label htmlFor="token" className="form-label">
                    Reset Token
                  </label>
                  <input
                    type="text"
                    id="token"
                    className="form-input"
                    placeholder="Enter your reset token"
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    disabled={isValidating}
                  />
                  <small className="form-helper-text">
                    Paste the reset token you received
                  </small>
                </div>
              )}

              {isValidating && (
                <div className="auth-info">
                  Validating token...
                </div>
              )}
              
              {tokenValid === false && (
                <div className="auth-error">
                  Invalid or expired reset token. Please request a new password reset.
                  <br />
                  <Link to="/login" className="auth-link-btn">
                    ← Back to Sign In
                  </Link>
                </div>
              )}

              {tokenValid === true && (
                <form onSubmit={handleSubmit} className="auth-form-fields">
                  {error && (
                    <div className="auth-error" role="alert">
                      {error}
                    </div>
                  )}
                  
                  <div className="form-group">
                    <label htmlFor="password" className="form-label">
                      {t('auth.newPassword')}
                    </label>
                    <div className="password-input-container">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          if (error) setError('');
                        }}
                        required
                        className="form-input"
                        placeholder="Enter new password"
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? '👁️' : '👁️‍🗨️'}
                      </button>
                    </div>
                    <small className="form-helper-text">
                      Password must be at least 6 characters long
                    </small>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="confirmPassword" className="form-label">
                      {t('auth.confirmPassword')}
                    </label>
                    <div className="password-input-container">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          if (error) setError('');
                        }}
                        required
                        className="form-input"
                        placeholder="Confirm new password"
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                      </button>
                    </div>
                  </div>
                  
                  <button 
                    type="submit" 
                    className="auth-submit-btn"
                    disabled={isLoading || !password || !confirmPassword}
                  >
                    {isLoading ? t('common.loading') : t('auth.resetPassword')}
                  </button>
                </form>
              )}
              
              <div className="auth-form-footer">
                <div className="auth-switch">
                  <Link to="/login" className="auth-link-btn">
                    ← {t('auth.backToLogin')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
